import { useEffect, useMemo, useState } from "react";
import type { GigListItemResponse, GigRequestRequest } from "../../../types/gigTypes.ts";
import {
  formatDateTimeInTimeZone,
  formatLongDateInTimeZone,
  getDateTimePartsInTimeZone,
  resolveGigTimeZone,
  toDateTimeLocalInputInTimeZone,
  toMinutePrecision,
  toZonedIsoString,
  zonedDateTimePartsToDate,
} from "../../../utils/DateUtils.ts";
import { formatGigCrewSummary } from "../../../utils/GigCrewUtils.ts";
import { formatGigScheduleSummary } from "../../../utils/GigScheduleUtils.ts";
import Button from "../../ui/Button/Button.tsx";
import Modal from "../../ui/Modal/Modal.tsx";

const PRE_ACCEPTANCE_CONTACT_INFO_NOTICE =
  "As noted in our Terms and for safety reasons, do not share phone numbers, email addresses, social handles, or other personal contact information before the gig is accepted.";

const isSameDateInTimeZone = (startTime: string, endTime: string, timeZone: string) => {
  const startDate = getDateTimePartsInTimeZone(new Date(startTime), timeZone);
  const endDate = getDateTimePartsInTimeZone(new Date(endTime), timeZone);

  return Boolean(startDate && endDate)
    && startDate?.year === endDate?.year
    && startDate?.month === endDate?.month
    && startDate?.day === endDate?.day;
};

const parseTimeInputParts = (value: string) => {
  const [hoursText, minutesText] = value.split(":");
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return { hours, minutes };
};

const formatTimeValue = (value: string) => {
  const parts = parseTimeInputParts(value);
  if (!parts) {
    return value;
  }

  const suffix = parts.hours >= 12 ? "pm" : "am";
  const normalizedHours = parts.hours % 12 || 12;
  return parts.minutes === 0
    ? `${normalizedHours}${suffix}`
    : `${normalizedHours}:${String(parts.minutes).padStart(2, "0")}${suffix}`;
};

const getPreferredTimeDate = (timeValue: string, referenceTime: string, timeZone: string) => {
  const timeParts = parseTimeInputParts(timeValue);
  const referenceParts = getDateTimePartsInTimeZone(new Date(referenceTime), timeZone);
  if (!timeParts || !referenceParts) {
    return null;
  }

  return zonedDateTimePartsToDate({
    year: referenceParts.year,
    month: referenceParts.month,
    day: referenceParts.day,
    hour: timeParts.hours,
    minute: timeParts.minutes,
    second: 0,
    millisecond: 0,
  }, timeZone);
};

const getShortTimeZoneName = (value: Date | string, timeZone: string) => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return timeZone;
  }

  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    timeZoneName: "short",
  }).formatToParts(parsed).find((part) => part.type === "timeZoneName")?.value ?? timeZone;
};

const formatPreferredTimeRangeLabel = (gig: GigListItemResponse, timeZone: string) => {
  if (!gig.preferredStartTime || !gig.preferredEndTime || !gig.startTime || !gig.endTime) {
    return null;
  }

  const preferredStartDate = getPreferredTimeDate(gig.preferredStartTime, gig.startTime, timeZone);
  const preferredEndDate = getPreferredTimeDate(gig.preferredEndTime, gig.startTime, timeZone);
  const startLabel = getShortTimeZoneName(preferredStartDate ?? gig.startTime, timeZone);
  const endLabel = getShortTimeZoneName(preferredEndDate ?? gig.endTime, timeZone);
  const timeZoneLabel = startLabel === endLabel ? startLabel : `${startLabel}/${endLabel}`;

  return `${formatTimeValue(gig.preferredStartTime)} - ${formatTimeValue(gig.preferredEndTime)} ${timeZoneLabel}`;
};

const formatDurationForSentence = (minutes: number | null) => {
  if (minutes === null || minutes <= 0) {
    return "not set";
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const hourLabel = hours === 1 ? "1 hr" : `${hours} hrs`;
  const minuteLabel = remainingMinutes === 1 ? "1 min" : `${remainingMinutes} mins`;

  if (hours > 0 && remainingMinutes > 0) {
    return `${hourLabel} ${minuteLabel}`;
  }
  if (hours > 0) {
    return hourLabel;
  }
  return minuteLabel;
};

const renderTimingSummary = (gig: GigListItemResponse, timeZone: string) => {
  const durationLabel = formatDurationForSentence(gig.estimatedDurationMinutes);

  if (gig.scheduleType === "FIXED" && gig.startTime && gig.endTime) {
    const preferredTimeLabel = formatPreferredTimeRangeLabel(gig, timeZone);
    const timingLabel = isSameDateInTimeZone(gig.startTime, gig.endTime, timeZone) ? (
      <>
        on <strong>{formatLongDateInTimeZone(gig.startTime, timeZone)}</strong>
      </>
    ) : (
      <>
        between <strong>{formatLongDateInTimeZone(gig.startTime, timeZone)}</strong> and{" "}
        <strong>{formatLongDateInTimeZone(gig.endTime, timeZone)}</strong>
      </>
    );

    return (
      <p>
        The poster wants the gig done {timingLabel}
        {preferredTimeLabel ? (
          <>
            , preferred time <strong>{preferredTimeLabel}</strong>
          </>
        ) : null}
        , and estimates the time to complete the gig to be <strong>{durationLabel}</strong>.
      </p>
    );
  }

  return (
    <p>
      The poster listed the timing as <strong>{formatGigScheduleSummary(gig)}</strong> and estimates the time to complete
      the gig to be <strong>{durationLabel}</strong>.
    </p>
  );
};

type RequestGigModalProps = {
  isOpen: boolean;
  gig: GigListItemResponse | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: GigRequestRequest) => Promise<void>;
};

const RequestGigModal = ({ isOpen, gig, isSubmitting, onClose, onSubmit }: RequestGigModalProps) => {
  const [proposedStartTime, setProposedStartTime] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const isFlexibleGig = gig?.scheduleType === "FLEXIBLE";
  const gigTimeZone = resolveGigTimeZone(gig?.timeZone ?? null);
  const availabilityStartLocal = toDateTimeLocalInputInTimeZone(gig?.startTime ?? null, gigTimeZone);
  const availabilityEndLocal = toDateTimeLocalInputInTimeZone(gig?.endTime ?? null, gigTimeZone);
  const currentMinuteInput = toDateTimeLocalInputInTimeZone(toMinutePrecision(new Date()).toISOString(), gigTimeZone);
  const estimatedDurationMinutes = typeof gig?.estimatedDurationMinutes === "number" && gig.estimatedDurationMinutes > 0
    ? gig.estimatedDurationMinutes
    : null;
  const computedEndTime = useMemo(() => {
    const proposedStartTimeIso = toZonedIsoString(proposedStartTime, gigTimeZone);
    if (!proposedStartTimeIso || estimatedDurationMinutes === null) {
      return null;
    }

    return new Date(new Date(proposedStartTimeIso).getTime() + estimatedDurationMinutes * 60_000).toISOString();
  }, [estimatedDurationMinutes, gigTimeZone, proposedStartTime]);
  const computedEndLabel = useMemo(
    () => formatDateTimeInTimeZone(computedEndTime, gigTimeZone, null),
    [computedEndTime, gigTimeZone],
  );

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!isOpen || !gig) {
      setProposedStartTime("");
      setMessage("");
      setError(null);
      return;
    }

    if (gig.scheduleType === "FIXED") {
      setProposedStartTime(toDateTimeLocalInputInTimeZone(gig.startTime, gigTimeZone));
    } else {
      setProposedStartTime("");
    }
    setMessage("");
    setError(null);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [gig, gigTimeZone, isOpen]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!gig) {
      return;
    }

    setError(null);

    const proposedStartTimeIso = toZonedIsoString(proposedStartTime, gigTimeZone);
    const proposedEndTimeIso = computedEndTime;
    if (!proposedStartTimeIso) {
      setError("Choose a start time for this gig.");
      return;
    }
    if (!proposedEndTimeIso) {
      setError("Unable to calculate the end time for this gig.");
      return;
    }

    const proposedStartDate = toMinutePrecision(new Date(proposedStartTimeIso));
    const proposedEndDate = toMinutePrecision(new Date(proposedEndTimeIso));
    if (proposedEndDate < proposedStartDate) {
      setError("Proposed end time must be after start time.");
      return;
    }

    const availableFromDate = gig.startTime ? toMinutePrecision(new Date(gig.startTime)) : null;
    const availableUntilDate = gig.endTime ? toMinutePrecision(new Date(gig.endTime)) : null;
    if (availableFromDate && proposedStartDate < availableFromDate) {
      setError("Proposed start must be within the gig availability window.");
      return;
    }
    if (availableUntilDate && proposedStartDate > availableUntilDate) {
      setError("Proposed start must be within the gig availability window.");
      return;
    }

    try {
      await onSubmit({
        message: message.trim().length > 0 ? message.trim() : undefined,
        proposedStartTime: proposedStartTimeIso,
        proposedEndTime: proposedEndTimeIso,
      });
    } catch {
      // Parent handles feedback; keep modal open for correction/retry.
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Apply for Gig">
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
        {gig ? (
          <div className="space-y-1 text-sm text-myteengig-text-primary">
            {renderTimingSummary(gig, gigTimeZone)}
            <p>
              Crew: <strong>{formatGigCrewSummary(gig)}</strong>
            </p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="request-proposed-start" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
              When do you want to complete this gig?
            </label>
            <input
              id="request-proposed-start"
              type="datetime-local"
              required
              min={isFlexibleGig ? currentMinuteInput : availabilityStartLocal || undefined}
              max={isFlexibleGig ? undefined : availabilityEndLocal || undefined}
              value={proposedStartTime}
              onChange={(event) => {
                setProposedStartTime(event.target.value);
                setError(null);
              }}
              className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            />
            <p className="mt-1 text-xs text-myteengig-text-muted">
              {isFlexibleGig
                ? "Choose the start time that works for you."
                : "Please choose a start time inside the poster's time window."}
            </p>
          </div>
          <div className="text-sm text-myteengig-text-primary">
            <span className="mb-1 block font-medium">Expected End Time</span>
            <div className="rounded-md border border-myteengig-border bg-myteengig-background px-3 py-2">
              <p className="font-medium">{computedEndLabel ?? "Choose a start time to calculate the end."}</p>
              <p className="mt-1 text-xs text-myteengig-text-muted">
                Based on the estimated time to completion: {formatDurationForSentence(gig?.estimatedDurationMinutes ?? null)}.
              </p>
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="request-message" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            Message (optional)
          </label>
          <p className="mb-2 rounded-xl border border-myteengig-border bg-myteengig-background px-3 py-2 text-xs leading-relaxed text-myteengig-text-muted">
            {PRE_ACCEPTANCE_CONTACT_INFO_NOTICE}{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-myteengig-primary underline underline-offset-2 hover:text-myteengig-primary-hover"
            >
              Review the Terms
            </a>
            .
          </p>
          <textarea
            id="request-message"
            maxLength={5000}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            rows={4}
            placeholder="Anything the poster should know about you or your availability?"
          />
        </div>

        {error ? <p className="text-sm font-medium text-red-600">{error}</p> : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Applying..." : "Apply for Gig"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RequestGigModal;
